import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import AccuracyService from "@app/projects/network-assessment/accuracy/accuracyService";
import { AnalysisSpecialtyGroup, ResultRegion } from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";
import { Resource, ResourceStatus } from "@app/resources";
import React, { FC, useEffect, useRef, useState } from "react";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { formatDate } from "@app/shared/formatDateTime";
import CountyProjectContainer from "@app/projects/CountyProjectContainer";
import ProjectOptions from "@app/projects/projectOptions";
import { ProjectRunConfigurationResult, createRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectService from "@app/projects/projectService";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import GeographyService from "@app/geography/geographyService";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import TemplateService from "@app/templates/templateService";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy/adequacyDetail";
import { Details } from "@app/admin/provider-results/providers/Details";

const ProviderResults: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const routeMatch = useRouteMatch();
  const accuracyService = useRef(new AccuracyService()).current;
  const geographyService = useRef(new GeographyService()).current;
  const projectService = useRef(new ProjectService()).current;
  const specialtyGroupExceptionsService = useRef(new ExceptionsService()).current;
  const templateService = useRef(new TemplateService()).current;
  const networkAssessmentAnalysisType = NetworkAssessmentAnalysisType.ServiceArea;
  const runConfigurationMapRef = useRef<{ [runConfigurationIdNetworkAnalyaisTypeKey: string]: ProjectRunConfigurationResult }>({});
  const [regionSetsMap, setRegionSetsMap] = useState<Map<number, SelectedRegionSet>>(new Map<number, SelectedRegionSet>());

  const [accessSummary, setAccessSummary] = useState<AccessSummary>();
  const [projectResources, setProjectResources] = useState<Resource[]>([]);
  const [regionSetId, setRegionSetId] = useState<number>();
  const [runConfiguration, setRunConfiguration] = useState<ProjectRunConfigurationResult>();
  const [selectedProject, setSelectedProject] = useState<ProjectOptions>();
  const [specialtyGroupCode, setSpecialtyGroupCode] = useState<string>();

  useEffect(() => {
    projectService.getProjectResources(ResourceStatus.Active).then((newProjectResources) => {
      setProjectResources(newProjectResources);
    });
  }, []);

  let serviceArea: ResultRegion = null;
  if (runConfiguration && runConfiguration.serviceArea) {
    //need to create a new result region of only the state region sets for display
    serviceArea = {
      displayName: runConfiguration.serviceArea.displayName,
      regionCode: runConfiguration.serviceArea.regionCode,
      regionGranularity: RegionGranularity.State,
      regionSetId: runConfiguration.serviceArea.regionSetId,
      subRegions: runConfiguration.serviceArea.subRegions.filter(
        (resultRegion) => resultRegion.regionGranularity === RegionGranularity.State),
    } as ResultRegion;
  }

  //the specialty groups with provider sets are all the specialty groups available to the template
  //minus any that are excluded
  //(runConfiguration.specialtyGroups is all the specialty group codes available to the template)
  let availableSpecialtyGroups: AnalysisSpecialtyGroup[] = null;
  if (runConfiguration && runConfiguration.specialtyGroups) {
    const excludedSpecialtyCodes = runConfiguration.excludedSpecialtyGroupCodes;
    availableSpecialtyGroups = runConfiguration.specialtyGroups.filter((item) => !excludedSpecialtyCodes.includes(item.code));
  }

  let key: string = null;
  if (selectedProject && runConfiguration && regionSetId && specialtyGroupCode) {
    //set a key to re-render the provider panel subcomponents if any of the dropdown values change
    key = `${selectedProject.projectId}:${runConfiguration.projectRunConfigurationId}:${regionSetId}:${specialtyGroupCode}`;
  }

  const renderProjectList = () => {
    return (
      <QESSelect
        label="Projects"
        className="form-control"
        onChange={(e) => handleProjectChange(e.currentTarget.value)}
        testId="provider-results__project-list"
      >
        <option value="">Select One</option>
        {
          projectResources.map((resource) => {
            const key: number = resource.resourceId;
            return (
              <option key={key} value={key}>
                {resource.name}
              </option>
            );
          })
        }
      </QESSelect>
    );
  }

  const renderProjectConfigurationsList = () => {
    const selectedValue: string = runConfiguration ? runConfiguration.projectRunConfigurationId.toString() : "";

    return (
      <QESSelect
        className="form-control"
        value={selectedValue}
        onChange={(e) => handleProjectRunConfigurationsChange(e.currentTarget.value)}
        label="Run Configurations"
        testId="provider-results__project-configuration-list"
      >
        <option value="">Select One</option>
        {
          selectedProject.projectRunConfigurations.map((config) => {
            const key: number = config.projectRunConfigurationId;
            if (config.datePublished) {
              const date = formatDate(config.datePublished);
              return (
                <option key={key} value={key}>
                  {config.publishComment ? (date + " - " + config.publishComment) : (date)}
                </option>
              );
            } else {
              return (
                <option key={key} value={key} label="Unpublished">
                  Unpublished
                </option>
              );
            }
          })
        });
      </QESSelect>
    );
  }

  const renderResultRegions = (resultRegion: ResultRegion, level: number): React.ReactNode => {
    return (
      <React.Fragment>
        {
          resultRegion.subRegions && resultRegion.subRegions.length > 0 &&
          renderResultRegionList(resultRegion, level)
        }
        {regionSetsMap.has(resultRegion.regionSetId) &&
          renderResultRegions(regionSetsMap.get(resultRegion.regionSetId).subRegion, level + 1)
        }
      </React.Fragment>
    );
  }

  const renderResultRegionList = (resultRegion: ResultRegion, level: number) => {
    //the granularity should be the same for all the subregions
    const granularity: RegionGranularity = resultRegion.subRegions[0].regionGranularity;

    const selectedValue: string =
      regionSetsMap.has(resultRegion.regionSetId) ? regionSetsMap.get(resultRegion.regionSetId).subRegion.regionSetId.toString() : "";

    return (
      <div key={resultRegion.regionSetId}>
        <div className="form-group row">
          <div className="col-6">
            <QESSelect
              label={granularity.charAt(0).toUpperCase() + granularity.slice(1)}
              className="form-control"
              value={selectedValue}
              onChange={(e) => handleResultRegionChange(resultRegion, e.currentTarget.value, level)}
              testId="provider-results__granularity"
            >
              <option value="">Select One</option>
              {
                resultRegion.subRegions.map((region) => {
                  const key: number = region.regionSetId;
                  return (
                    <option key={key} value={key}>
                      {region.displayName}
                    </option>
                  );
                })
              }
            </QESSelect>
          </div>
        </div>
      </div>
    );
  }

  const renderSpecialtyGroupList = (specialtyGroupCodes: AnalysisSpecialtyGroup[]) => {
    const selectedValue: string = specialtyGroupCode ? specialtyGroupCode : "";

    return (
      <div>
        <div className="form-group row">
          <div className="col-6">
            <QESSelect
              label="Specialty Groups"
              className="form-control"
              value={selectedValue}
              onChange={(e) => handleSpecialtyGroupChange(e.currentTarget.value)}
              testId="provider-results__specialty-groups"
            >
              <option value="">Select One</option>
              {
                specialtyGroupCodes.map((specialtyGroup) => {
                  const key: string = specialtyGroup.code;
                  return (
                    <option key={key} value={key}>
                      {specialtyGroup.name}
                    </option>
                  );
                })
              }
            </QESSelect>
          </div>
        </div>
      </div>
    );
  }

  const handleProjectChange = async (projectId: string) => {
    setRegionSetsMap(new Map<number, SelectedRegionSet>());

    const numProjectId = Number(projectId);

    if (!numProjectId) {
      setAccessSummary(null);
      setRegionSetId(null);
      setRunConfiguration(null);
      setSelectedProject(null);
      setSpecialtyGroupCode(null);
      return;
    }

    const newSelectedProject = (await projectService.getById(numProjectId));
    setAccessSummary(null);
    setRegionSetId(null);
    setRunConfiguration(null);
    setSelectedProject(newSelectedProject);
    setSpecialtyGroupCode(null);
  }

  const handleProjectRunConfigurationsChange = async (runConfigurationId: string) => {
    setRegionSetsMap(new Map<number, SelectedRegionSet>());
    const numRunConfigurationId = Number(runConfigurationId);

    if (!numRunConfigurationId) {
      setAccessSummary(null);
      setRegionSetId(null);
      setRunConfiguration(null);
      setSpecialtyGroupCode(null);
      return;
    }

    const runKey: string = runConfigurationKey(runConfigurationId, networkAssessmentAnalysisType);

    let newRunConfiguration: ProjectRunConfigurationResult = runConfigurationMapRef.current[runKey];
    if (!newRunConfiguration) {
      newRunConfiguration = await createRunConfigurationResult(
        numRunConfigurationId,
        selectedProject,
        projectService,
        specialtyGroupExceptionsService,
        accuracyService,
        templateService,
        geographyService,
        networkAssessmentAnalysisType);
    }

    runConfigurationMapRef.current[runKey] = newRunConfiguration;
    setAccessSummary(null);
    setRegionSetId(null);
    setRunConfiguration(newRunConfiguration);
    setSpecialtyGroupCode(null);
  }

  const handleResultRegionChange = async (resultRegion: ResultRegion, regionId: string, level: number) => {
    removeHigherGranularityRegionSetFromMap(level);
    const numRegionId = Number(regionId);
    const newRegionSetsMap = new Map(regionSetsMap);

    if (!numRegionId) {
      setAccessSummary(null);
      setRegionSetId(null);
      return;
    }

    const subRegion: ResultRegion = resultRegion.subRegions.find((r: ResultRegion) => {
      return r.regionSetId === numRegionId;
    });

    newRegionSetsMap.set(resultRegion.regionSetId, new SelectedRegionSet(subRegion, level));
    setRegionSetsMap(newRegionSetsMap);

    if (subRegion.subRegions && subRegion.subRegions.length > 0) {
      setAccessSummary(null);
      setRegionSetId(null);
    } else {
      //no more subregions so update state with regionId and accessSummary
      const adequacyDetails: AdequacyDetail[] = await runConfiguration.getAdequacyDetails(subRegion);
      let newAccessSummary: AccessSummary = null;
      if (adequacyDetails && adequacyDetails.length > 0) {
        newAccessSummary = adequacyDetails[0].accessSummary;
      }

      setAccessSummary(newAccessSummary);
      setRegionSetId(numRegionId);
    }
  }

  const removeHigherGranularityRegionSetFromMap = (level: number) => {
    const newRegionSetsMap = new Map<number, SelectedRegionSet>();
    //  have to remove all regionSetIds that are at a higher level
    //(higher level meaning they are at a higher granularity
    //and thus a child of the result region being changed)
    //NOTE: If the granularity enum was numeric then it could be
    //used instead of an iterator "level", but it's a string enum.
    for (const entry of regionSetsMap.entries()) {
      const key = entry[0];
      const value = entry[1];

      if (value.level < level) {
        newRegionSetsMap.set(key, value);
      }
    }

    setRegionSetsMap(newRegionSetsMap);
  }

  const handleSpecialtyGroupChange = (specialtyGroupId: string) => {
    if (specialtyGroupId === "") {
      setSpecialtyGroupCode(null);
      return;
    }

    setSpecialtyGroupCode(specialtyGroupId);
  }

  const runConfigurationKey = (runConfigurationId: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): string => {
    return `${runConfigurationId}:${networkAssessmentAnalysisType}`;
  }

  return (
    <div className="mb-20">
      <div className="container" >
        <form>
          <div>
            <div className="form-group row">
              <div className="col-6">
                {renderProjectList()}
              </div>
              {selectedProject && selectedProject.projectRunConfigurations && selectedProject.projectRunConfigurations.length > 0 &&
                <div className="col-6">
                  {renderProjectConfigurationsList()}
                </div>
              }
            </div>
          </div>
          {serviceArea &&
            renderResultRegions(serviceArea, 1)
          }
          {availableSpecialtyGroups && availableSpecialtyGroups.length > 0 &&
            renderSpecialtyGroupList(availableSpecialtyGroups)
          }
        </form>
      </div >
      {key &&
        <div key={key} className="container">
          <CountyProjectContainer projectId={selectedProject.projectId}>
            <Details accessSummary={accessSummary} regionSetId={regionSetId} specialtyGroupCode={specialtyGroupCode} history={history} location={location} match={routeMatch} />
          </CountyProjectContainer>
        </div>
      }
    </div>
  );
}

class SelectedRegionSet {
  public readonly subRegion: ResultRegion;
  public readonly level: number;

  constructor(subRegion: ResultRegion, level: number) {
    this.subRegion = subRegion;
    this.level = level;
  }
}

export default ProviderResults;
